import { observer } from 'mobx-react';
import { useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, Typography } from '@material-ui/core';
import { StandardDialog, SignatureFieldHandle } from '@roc/ui';
import { CustomTaskDialog } from '@roc/feature-general-tasks';
import { useStore } from '@roc/client-portal-shared/hooks';
import { BorrowerExtensionSigningService } from '../../../services/documentForms/borrowerExtensionSigningService';
import {
  ExtensionAgreementContent,
  ExtensionAgreementSignedMessage,
  ExtensionAgreementUnavailableMessage,
  SigningInfo,
} from './extensionAgreementContent';

interface Props {
  open?: boolean;
  onClose?: () => void;
  loanExtensionId?: number;
  taskId?: number;
  onSigned?: () => void;
}

export const BorrowerExtensionAgreementDialog = observer(
  ({ open, onClose, loanExtensionId, taskId, onSigned }: Props) => {
    const { globalStore } = useStore();
    const service = useRef(new BorrowerExtensionSigningService()).current;
    const signatureRef = useRef<SignatureFieldHandle>(null);

    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [info, setInfo] = useState<SigningInfo | null>(null);
    const [error, setError] = useState('');
    const [completed, setCompleted] = useState(false);

    const resolveErrorMessage = (e: any, fallback: string) =>
      e?.error?.response?.data?.error?.message ||
      e?.response?.data?.error?.message ||
      fallback;

    useEffect(() => {
      if (!open) {
        return;
      }
      if (!loanExtensionId) {
        setInfo(null);
        setError('We could not identify the extension for this agreement.');
        return;
      }
      loadInfo();
    }, [open, loanExtensionId]);

    const loadInfo = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await service.getExtensionSigningInfo(loanExtensionId);
        const data: SigningInfo = response?.data?.data ?? response?.data;
        setInfo(data || {});
        setCompleted(Boolean(data?.alreadySigned));
      } catch (e) {
        setError(
          resolveErrorMessage(e, 'We could not load the Extension Agreement. Please try again later.')
        );
      } finally {
        setLoading(false);
      }
    };

    const handleSubmit = async () => {
      if (!signatureRef.current?.isSigned()) {
        setError('Please sign the document before submitting it.');
        return;
      }
      setError('');
      setSubmitting(true);
      try {
        await service.signExtensionAgreement({
          loanExtensionId,
          signature: signatureRef.current.getSignature(),
          signerName: info?.borrowerName,
          userAgent: navigator.userAgent,
        });
        setCompleted(true);
        globalStore.notificationStore.showSuccessNotification({
          message: 'Your Extension Agreement has been signed successfully.',
        });
        onSigned?.();
      } catch (e) {
        setError(
          resolveErrorMessage(e, 'There was an error submitting your signature. Please try again.')
        );

        const status = e?.error?.response?.status ?? e?.response?.status;
        if (status === 400) {
          await loadInfo();
        }
      } finally {
        setSubmitting(false);
      }
    };

    const dialogContent = loading ? (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    ) : completed ? (
      <ExtensionAgreementSignedMessage borrowerName={info?.borrowerName} />
    ) : info && info.hasSigningRequest === false ? (
      <Box p={4} textAlign="center">
        <Typography variant="body1">
          No signature is required from you for this Extension Agreement.
        </Typography>
      </Box>
    ) : info && !info.alreadySigned && info.canSign === false ? (
      <ExtensionAgreementUnavailableMessage message={info.unavailableReason} />
    ) : !info ? (
      <Box p={4} textAlign="center">
        <Typography variant="body1">
          {error || 'We could not load the Extension Agreement. Please try again later.'}
        </Typography>
      </Box>
    ) : (
      <ExtensionAgreementContent
        info={info}
        signatureRef={signatureRef}
        onSubmit={handleSubmit}
        submitting={submitting}
        error={error}
      />
    );

    return taskId ? (
      <CustomTaskDialog
        title={'Extension Agreement'}
        open={open}
        maxWidth={'md'}
        handleClose={onClose}
        dialogContent={dialogContent}
        taskId={taskId}
      />
    ) : (
      <StandardDialog
        title={'Extension Agreement'}
        open={open}
        maxWidth={'md'}
        handleClose={onClose}
        dialogContent={dialogContent}
      />
    );
  }
);

export default BorrowerExtensionAgreementDialog;
